import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Lock, Plus, Save, Archive, Layers } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const numField =
  'w-28 bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm tabular-nums ' +
  'focus:outline-none focus:border-slate-900';

/** Owner console: what each package costs and which modules it contains. */
const PackagesPage = () => {
  const { authHeader } = useAuth();
  const [modules, setModules] = useState([]);
  const [core, setCore] = useState([]);
  const [plans, setPlans] = useState([]);
  const [counts, setCounts] = useState({});
  const [draft, setDraft] = useState({});     // key -> edited plan
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/platform/catalog`, { headers: authHeader() });
    setModules(data.modules || []);
    setCore(data.core_modules || []);
    setPlans(data.plans || []);
    setCounts(data.tenant_counts || {});
    setDraft(Object.fromEntries((data.plans || []).map((p) => [p.key, { ...p }])));
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const live = useMemo(() => plans.filter((p) => !p.archived), [plans]);
  const drafts = useMemo(() => plans.filter((p) => p.archived), [plans]);
  // Drafts and retired packages stay editable so you can price them and set
  // their modules before putting them on sale.
  const editable = useMemo(() => [...live, ...drafts], [live, drafts]);
  const groups = useMemo(() => {
    const out = [];
    modules.forEach((m) => {
      const g = out.find((x) => x.name === m.group);
      if (g) g.items.push(m); else out.push({ name: m.group, items: [m] });
    });
    return out;
  }, [modules]);

  const edit = (key, patch) => setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const toggleModule = (key, moduleKey) => {
    if (core.includes(moduleKey)) return;
    const row = draft[key];
    const has = (row.features || []).includes(moduleKey);
    edit(key, {
      features: has ? row.features.filter((f) => f !== moduleKey)
                    : [...(row.features || []), moduleKey],
    });
  };

  const dirty = (key) => JSON.stringify(draft[key]) !== JSON.stringify(plans.find((p) => p.key === key));

  const save = async (key) => {
    setSavingKey(key);
    try {
      const d = draft[key];
      await axios.patch(`${API}/platform/catalog/plans/${key}`, {
        name: d.name, tagline: d.tagline, price_monthly: Number(d.price_monthly),
        price_yearly: Number(d.price_yearly), max_staff: Number(d.max_staff),
        features: d.features,
      }, { headers: authHeader() });
      toast.success(`${d.name} saved`, {
        description: 'New signups get this straight away. Workshops already on it keep what they have.',
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the package');
    } finally {
      setSavingKey('');
    }
  };

  const archive = async (key, name) => {
    try {
      await axios.delete(`${API}/platform/catalog/plans/${key}`, { headers: authHeader() });
      toast.success(`${name} retired — it is no longer offered to new workshops`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not retire the package');
    }
  };

  const publish = async (key, name) => {
    const d = draft[key] || {};
    if (!Number(d.price_monthly) && !Number(d.price_yearly)) {
      toast.error('Set a price before putting this package on sale');
      return;
    }
    try {
      await axios.patch(`${API}/platform/catalog/plans/${key}`, {
        name: d.name, tagline: d.tagline, price_monthly: Number(d.price_monthly),
        price_yearly: Number(d.price_yearly), max_staff: Number(d.max_staff),
        features: d.features, archived: false,
      }, { headers: authHeader() });
      toast.success(`${name} is now on sale`, {
        description: 'It appears on the signup page and in the plan picker.',
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not publish the package');
    }
  };

  const addPackage = async () => {
    if (newName.trim().length < 2) return;
    setCreating(true);
    try {
      await axios.post(`${API}/platform/catalog/plans`, {
        name: newName.trim(), price_monthly: 0, price_yearly: 0, features: core,
      }, { headers: authHeader() });
      toast.success(`${newName.trim()} created as a draft — price it, pick its modules, then put it on sale`);
      setNewName('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the package');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Loading packages…
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="packages-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Layers size={22} className="text-teal-600" /> Packages &amp; Modules
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Set what each package costs and which modules it includes. Changes apply to
          <b> new signups</b>; workshops already on a package keep what they joined with
          until you move them.
        </p>
      </div>

      {/* Prices */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="packages-cards">
        {editable.map((p) => (
          <div key={p.key} className="rounded-2xl border border-slate-200 bg-white p-5"
               data-testid={`package-card-${p.key}`}>
            <div className="flex items-start justify-between gap-2">
              <input
                className="text-lg font-semibold bg-transparent border-b border-transparent hover:border-slate-300 focus:border-slate-900 focus:outline-none w-full"
                value={draft[p.key]?.name || ''}
                onChange={(e) => edit(p.key, { name: e.target.value })}
                data-testid={`package-name-${p.key}`}
              />
              <div className="flex items-center gap-1 shrink-0">
                {p.archived && (
                  <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full"
                        data-testid={`package-draft-${p.key}`}>
                    {p.is_custom && !p.price_monthly ? 'Draft' : 'Not on sale'}
                  </span>
                )}
                {p.is_custom && (
                  <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                    Custom
                  </span>
                )}
              </div>
            </div>
            <input
              className="mt-1 w-full text-sm text-slate-500 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-slate-900 focus:outline-none"
              placeholder="One line describing who it is for"
              value={draft[p.key]?.tagline || ''}
              onChange={(e) => edit(p.key, { tagline: e.target.value })}
              data-testid={`package-tagline-${p.key}`}
            />

            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">Monthly</dt>
                <dd><input type="number" min="0" className={numField} value={draft[p.key]?.price_monthly ?? 0}
                           onChange={(e) => edit(p.key, { price_monthly: e.target.value })}
                           data-testid={`package-monthly-${p.key}`} /></dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">Yearly</dt>
                <dd><input type="number" min="0" className={numField} value={draft[p.key]?.price_yearly ?? 0}
                           onChange={(e) => edit(p.key, { price_yearly: e.target.value })}
                           data-testid={`package-yearly-${p.key}`} /></dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">Staff limit <span className="text-slate-400">(0 = unlimited)</span></dt>
                <dd><input type="number" min="0" className={numField} value={draft[p.key]?.max_staff ?? 0}
                           onChange={(e) => edit(p.key, { max_staff: e.target.value })}
                           data-testid={`package-staff-${p.key}`} /></dd>
              </div>
            </dl>

            <div className="mt-4 flex items-center justify-between gap-2 text-xs text-slate-500">
              <span data-testid={`package-count-${p.key}`}>
                {counts[p.key] || 0} workshop{(counts[p.key] || 0) === 1 ? '' : 's'} on this
              </span>
              <div className="flex items-center gap-2">
                {p.archived && (
                  <button onClick={() => publish(p.key, draft[p.key]?.name || p.name)}
                          className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 font-medium"
                          data-testid={`package-publish-${p.key}`}>
                    <Check size={13} /> Put on sale
                  </button>
                )}
                {!counts[p.key] && !p.archived && (
                  <button onClick={() => archive(p.key, p.name)}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-red-600"
                          data-testid={`package-archive-${p.key}`}>
                    <Archive size={13} /> Retire
                  </button>
                )}
                <button
                  onClick={() => save(p.key)}
                  disabled={!dirty(p.key) || savingKey === p.key}
                  className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-200 disabled:text-slate-400 text-white font-medium px-3 py-1.5 rounded-lg transition-colors"
                  data-testid={`package-save-${p.key}`}
                >
                  {savingKey === p.key ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Save
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* New package */}
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-5 flex flex-col justify-center"
             data-testid="package-create">
          <h3 className="text-base font-semibold text-slate-900">New package</h3>
          <p className="text-sm text-slate-500 mt-1">
            Starts with the core modules only — add the rest below.
          </p>
          <input
            className="mt-3 w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
            placeholder="e.g. Detailing Studio"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="package-new-name"
          />
          <button
            onClick={addPackage}
            disabled={creating || newName.trim().length < 2}
            className="mt-3 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
            data-testid="package-create-btn"
          >
            {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            Create package
          </button>
        </div>
      </div>

      {/* Module matrix */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="module-matrix">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left font-semibold text-slate-900 px-5 py-3">Module</th>
              {editable.map((p) => (
                <th key={p.key} className="px-4 py-3 text-center font-semibold text-slate-900 whitespace-nowrap">
                  {draft[p.key]?.name || p.name}
                  <div className="text-xs font-normal text-slate-400">
                    {p.archived ? 'not on sale' : `${inr(draft[p.key]?.price_monthly)}/mo`}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.name}>
                <tr className="bg-slate-50/80">
                  <td colSpan={editable.length + 1}
                      className="px-5 py-1.5 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                    {g.name}
                  </td>
                </tr>
                {g.items.map((m) => (
                  <tr key={m.key} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                    <td className="px-5 py-2.5">
                      <div className="font-medium text-slate-800 flex items-center gap-1.5">
                        {m.label}
                        {core.includes(m.key) && <Lock size={11} className="text-slate-400" />}
                      </div>
                      <div className="text-xs text-slate-500">{m.description}</div>
                    </td>
                    {editable.map((p) => {
                      const on = (draft[p.key]?.features || []).includes(m.key);
                      const locked = core.includes(m.key);
                      return (
                        <td key={p.key} className="px-4 py-2.5 text-center">
                          <button
                            type="button"
                            disabled={locked}
                            onClick={() => toggleModule(p.key, m.key)}
                            title={locked ? 'Every package needs this' : ''}
                            className={`w-7 h-7 rounded-lg border inline-flex items-center justify-center transition-colors ${
                              on ? 'bg-teal-500 border-teal-500 text-white'
                                 : 'bg-white border-slate-300 hover:border-slate-500'} ${
                              locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                            data-testid={`matrix-${p.key}-${m.key}`}
                            aria-label={`${m.label} in ${p.name}`}
                          >
                            {on && <Check size={15} />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Tick or untick a module, then press Save on that package's card above.
      </p>
    </div>
  );
};

export default PackagesPage;
